import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { Organization } from 'fhir/r4';
import { AuthService } from './auth/auth.service';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { HospitalService } from '../hospital-details/hospital.service';

@Injectable({ providedIn: 'root' })
export class OrganizationSettingsService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private hospitalService = inject(HospitalService);
  private backendUrl = inject(backendEndPointToken);
  private terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);

  private orgCache$?: Observable<Organization | null>;

  constructor() {
    // Clear cache if organization is updated via settings
    this.hospitalService.onOrganizationUpdated.subscribe((org) => {
      this.orgCache$ = of(org).pipe(shareReplay(1));
    });
  }

  getOrganization(): Observable<Organization | null> {
    if (this.orgCache$) return this.orgCache$;

    const orgId = this.auth.user.getValue()?.organizationId;
    if (!orgId) return of(null);

    this.orgCache$ = this.http.get<Organization>(`${this.backendUrl}/Organization/${orgId}`).pipe(
      shareReplay(1)
    );
    return this.orgCache$;
  }

  canManageManualReferenceRanges(): Observable<boolean> {
    return this.getOrganization().pipe(
      map(org => {
        if (!org) return false;

        const extUrl = `${this.terminologyBaseUrl}/StructureDefinition/manualReferenceRangePolicy`;
        const policyExt = org.extension?.find(e => e.url === extUrl);
        
        if (!policyExt) return false;

        const enabled = !!policyExt.extension?.find(e => e.url === 'enabled')?.valueBoolean;
        if (!enabled) return false;

        const allowedRolesStr = policyExt.extension?.find(e => e.url === 'allowedRoles')?.valueString || '';
        const allowedRoles = allowedRolesStr.split(',').map(r => r.trim().toLowerCase()).filter(r => r);

        if (allowedRoles.length === 0) return false;

        const user = this.auth.user.getValue();
        const activeRole = user?.role ? user.role.toString().toLowerCase() : '';
          
        return activeRole ? allowedRoles.includes(activeRole) : false;
      })
    );
  }
}
